import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, Container, Grid, Skeleton, Stack, Typography } from '@mui/material';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Page from 'src/components/Page';
import CustomDatePicker from 'src/components/data-picker/CustomDatePicker';
import useSettings from 'src/hooks/useSettings';
import { PATH_MANAGEMENT } from 'src/routes/paths';
import { getCharityDonations } from 'src/services/charityDonationServices';
import { convertDateToServerFormat } from 'src/utils/formatTime';
import { numberWithCommas } from 'src/utils/formatNumber';

const INITIAL_FILTERS = {
  from_date: convertDateToServerFormat(new Date(new Date().setDate(new Date().getDate() - 7))),
  to_date: convertDateToServerFormat(new Date()),
};

const CharityDonations = () => {
  const { themeStretch } = useSettings();
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const { data, isLoading } = useQuery(
    ['charityDonations', filters],
    () => getCharityDonations(filters),
    { keepPreviousData: true }
  );

  const report = data?.data?.data ?? data?.data ?? {};
  const totalAmount = report?.total_amount ?? 0;
  const donationsCount = report?.donations_count ?? 0;
  const currencyLabel = report?.currency === 'IRR' || !report?.currency ? 'ریال' : report.currency;

  return (
    <Page title="گزارش پرداختی‌های خیریه">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="گزارش پرداختی‌های خیریه"
          links={[
            { name: 'مدیریت', href: PATH_MANAGEMENT.management.root },
            { name: 'پرداختی‌های خیریه' },
          ]}
        />

        <Card sx={{ mb: 3 }}>
          <Stack
            spacing={2}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ p: 3 }}
          >
            <CustomDatePicker
              label="از تاریخ"
              value={filters.from_date}
              onChange={(value) =>
                setFilters((previous) => ({
                  ...previous,
                  from_date: convertDateToServerFormat(value),
                }))
              }
            />
            <CustomDatePicker
              label="تا تاریخ"
              value={filters.to_date}
              onChange={(value) =>
                setFilters((previous) => ({
                  ...previous,
                  to_date: convertDateToServerFormat(value),
                }))
              }
            />
          </Stack>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  مجموع کمک‌های پرداخت‌شده
                </Typography>
                {isLoading ? (
                  <Skeleton variant="text" width={220} height={52} />
                ) : (
                  <Typography variant="h4" color="text.primary" sx={{ mt: 1 }}>
                    {numberWithCommas(totalAmount)} {currencyLabel}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  تعداد پرداخت‌ها
                </Typography>
                {isLoading ? (
                  <Skeleton variant="text" width={100} height={52} />
                ) : (
                  <Typography variant="h4" sx={{ mt: 1 }}>
                    {numberWithCommas(donationsCount)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default CharityDonations;
